import CircleIcon from "@mui/icons-material/Circle";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import type { FilterEntry, FilterType } from "hooks/filters";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import React, { type FunctionComponent, useCallback, useContext } from "react";
import { AutocompleteFilterChip } from "./AutocompleteFilterChip";
import { ColorFilterChip } from "./ColorFilterChip";

export interface FilterChipsProps {
  filters: Array<FilterEntry>;
  onChange: (change: Array<FilterEntry>) => void;
  onFilterReset?: () => void;
  isSearchPage?: boolean;
  isFromResultsViewPage?: boolean;
  /**
   * Extracted image color shown as the "reference image" chip on the results
   * view. This is the primary `cie_lab` reference — distinct from any
   * user-entered `color` filter in `filters`, which renders as its own color
   * chip. Deleting this chip discards the upload-flow progress (the reference
   * image/palette) via {@link onReferenceColorDelete}; the `color` chip itself
   * behaves like a plain filter removal (see {@link onColorFilterDelete}).
   */
  hasReferenceColor?: boolean;
  onReferenceColorDelete?: () => void;
  /**
   * Delete handler for the `color` chip when `isFromResultsViewPage` is true.
   * Removing the Color:CIE-Lab chip on the Upload & Match results view is
   * gated behind a discard-confirm dialog, same as the reference image chip.
   */
  onColorFilterDelete?: () => void;
  t: (key: string) => string;
}

export const FilterChips = ({
  filters,
  onChange,
  onFilterReset,
  isSearchPage,
  isFromResultsViewPage,
  hasReferenceColor,
  onReferenceColorDelete,
  onColorFilterDelete,
  t,
}: FilterChipsProps) => {
  const { settings, setSettings } =
    useContext<SettingsContextValue>(SettingsContext);
  const onDelete = (type: FilterType) => {
    onChange(filters.filter((f) => f.type !== type));
    if (type === "cdc") setSettings({ ...settings, cdc: [] });
    if (type === "brand") setSettings({ ...settings, brand: [] });
  };

  const renderColorChip = useCallback(
    ({ value, onDelete }: FilterChipProps<any>) => {
      if (value.color) {
        return (
          <ColorFilterChip
            value={value.color}
            label={t("src_table_column_color")}
            onDelete={onDelete}
          />
        );
      } else if (value.finalColorCode && value.finalColorCode.length > 0) {
        return (
          <AutocompleteFilterChip
            value={value.finalColorCode}
            label={t("filter_color_code_covestro")}
            onDelete={onDelete}
          />
        );
      } else if (value.preliminaryColorCode || value.yearOfDevelopment) {
        return (
          <>
            {value.yearOfDevelopment && (
              <AutocompleteFilterChip
                value={value.yearOfDevelopment}
                label={t("filter_color_code_year_development")}
                onDelete={onDelete}
              />
            )}
            {value.preliminaryColorCode && (
              <AutocompleteFilterChip
                value={value.preliminaryColorCode}
                label={t("filter_color_code_preliminary")}
                onDelete={onDelete}
              />
            )}
          </>
        );
      }
      return null;
    },
    [t],
  );

  const referenceChip =
    isFromResultsViewPage && hasReferenceColor ? (
      <Chip
        label={t("src_filter_reference_image")}
        onDelete={onReferenceColorDelete}
      />
    ) : null;

  const activeFilters = filters
    .filter((i) => i.type !== "colorSystemCode")
    .filter((f) => chipFilterConfig[f.type].test?.(f) !== false);

  const colorSystemLabels: Record<string, string> = {
    ral_design_plus: "Matched for RAL DESIGN SYSTEM plus",
    ncs: "Matched for NCS",
  };

  const getFilterLabel = (filter: FilterEntry): string => {
    if (filter.type === "isColorSystem" && colorSystemLabels[filter.value]) {
      return colorSystemLabels[filter.value];
    }
    return t(chipFilterConfig[filter.type].label);
  };

  const renderFilter = (f: FilterEntry) => {
    if (f.type === "color") {
      // In Upload & Match the color filter is sourced from the Extracted
      // Color Palette, so removing it discards upload-flow progress rather
      // than just clearing a filter — onColorFilterDelete routes it through
      // the same confirm-discard flow as the Reference Image chip.
      return (
        <React.Fragment key={f.type}>
          {renderColorChip({
            value: f.value,
            label: "",
            onDelete: isFromResultsViewPage
              ? onColorFilterDelete
              : () => onDelete(f.type),
          })}
        </React.Fragment>
      );
    }

    const ChipComp = chipFilterConfig[f.type].render;
    const label = getFilterLabel(f);

    return (
      <ChipComp
        key={f.type}
        value={f.value}
        label={label}
        onDelete={() => onDelete(f.type)}
      />
    );
  };

  if (isFromResultsViewPage) {
    const hasActiveFilters = activeFilters.length > 0 || !!referenceChip;
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        <Typography
          component="span"
          fontWeight={500}
          fontSize="var(--body-b1-16-regular-size, 16px)"
          sx={{ color: "var(--alias-text-on-white-primary, #1a1a1a)" }}
        >
          {t("src_filter_active_filters_heading")}
        </Typography>
        {!hasActiveFilters && (
          <Typography
            fontStyle="italic"
            color="text.secondary"
            fontSize="var(--cf-local-13-size, 13px)"
          >
            {t("src_filter_inactive")}
          </Typography>
        )}
        <Box
          sx={{
            display: hasActiveFilters ? "flex" : "none",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            sx={{ flex: 1, minWidth: 0 }}
          >
            {referenceChip}
            {activeFilters.map(renderFilter)}
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <Stack direction="row" gap={1} flexWrap="wrap">
      {activeFilters.map(renderFilter)}
      {activeFilters.length === 0 && (
        <Stack direction="row" alignItems="center">
          <Typography
            fontStyle="italic"
            color="text.secondary"
            fontSize="var(--cf-local-13-size, 13px)"
          >
            {t("src_filter_inactive")}
          </Typography>
        </Stack>
      )}
      {!isSearchPage && (
        <Button sx={{ height: 32 }} onClick={onFilterReset} size="small">
          {t("src_filter_reset_action")}
        </Button>
      )}
    </Stack>
  );
};

const chipFilterConfig: Record<
  FilterType,
  {
    label: string;
    render: FunctionComponent<FilterChipProps<any>>;
    test?: (props: FilterEntry) => boolean;
  }
> = {
  colorStatus: {
    label: "filter_general_color_status",
    render: AutocompleteFilterChip,
  },
  substanceList: {
    label: "filter_general_substance",
    render: AutocompleteFilterChip,
  },
  brand: {
    label: "filter_grade_brand",
    render: AutocompleteFilterChip,
  },
  gradeShortName: {
    label: "filter_grade_short_name",
    render: AutocompleteFilterChip,
  },
  gradeNumber: {
    label: "filter_grade_number",
    render: AutocompleteFilterChip,
  },
  gradeProperty: {
    label: "filter_grade_properties",
    render: ({ ...props }) => (
      <AutocompleteFilterChip useValue={true} {...props} />
    ),
  },
  grade: {
    label: "filter_grade",
    render: AutocompleteFilterChip,
  },
  opacity: {
    label: "filter_color_opacity",
    render: AutocompleteFilterChip,
  },
  colorEffect: {
    label: "filter_color_effect",
    render: AutocompleteFilterChip,
  },
  colorTag: {
    label: "filter_color_tag",
    render: AutocompleteFilterChip,
  },
  colorGroup: {
    label: "filter_color_code_color_group",
    render: ({ value, ...props }) => (
      <Chip
        {...props}
        label={
          <Stack direction={"row"} mr={-0.5} alignItems={"center"}>
            <Typography fontSize="inherit" mr={0.25}>
              {props.label}:
            </Typography>
            {value.map((v: { color: string }) => (
              <CircleIcon key={v.color} style={{ color: v.color }} />
            ))}
          </Stack>
        }
      />
    ),
  },
  color: {
    label: "src_table_column_color",
    render: AutocompleteFilterChip,
    // A color filter carries a `colorSystem` even before the user enters a
    // value (picking a system in the dropdown seeds it). Only treat it as an
    // active filter once it actually holds a renderable value, so the "Active
    // Filters" section reflects what was really entered (CX-2860).
    test: (f) =>
      Boolean(
        f.value?.color ||
          (f.value?.finalColorCode?.length ?? 0) > 0 ||
          f.value?.yearOfDevelopment ||
          f.value?.preliminaryColorCode,
      ),
  },
  cdc: {
    label: "filter_general_cdc",
    render: AutocompleteFilterChip,
  },
  oemName: {
    label: "filter_general_oem_name",
    render: AutocompleteFilterChip,
  },
  customerName: {
    label: "filter_general_customer_name",
    render: AutocompleteFilterChip,
  },
  uvp: {
    label: "filter_sap_number",
    render: AutocompleteFilterChip,
  },
  hasDigitalSample: {
    label: "filter_omd_digital_samples",
    render: Chip,
  },
  hasLevel3Data: {
    label: "filter_omd_item_text",
    render: Chip,
  },
  industry: {
    label: "filter_industry",
    render: AutocompleteFilterChip,
  },
  application: {
    label: "filter_application",
    render: AutocompleteFilterChip,
  },
  applicationClass: {
    label: "filter_application_class",
    render: AutocompleteFilterChip,
  },
  texture: {
    label: "filter_omd_texture",
    render: AutocompleteFilterChip,
  },
  isColorSystem: {
    label: "Matched for RAL CLASSIC",
    render: Chip,
  },
  colorSystemCode: {
    label: "Matched for RAL Code",
    render: Chip,
  },
};

export interface FilterChipProps<T> {
  value: T;
  label: string;
  onDelete?: () => void;
  useValue?: boolean;
}
